import { FC, Suspense, useState } from "react";

import { Box, CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

import { ActiveProcedureItem } from "@/components/Procedures/ActiveProcedureItem";

import ComponentError from "@/helpers/components/Errors/Components";
import PaginatedSearchWrapper from "@/helpers/components/PaginatedSearchWrapper";

import { useFetchActiveProcedures } from "@/service/ProcedureQueries";

const Loader = () => (
	<Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
		<CircularProgress />
	</Box>
);

const PAGE_SIZE = 8;

export const ActiveProcedureDesign: FC = () => {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
	const { data, isPending } = useFetchActiveProcedures(page, PAGE_SIZE, search, sort);

	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<Sentry.ErrorBoundary
					onReset={reset}
					fallback={({ error, resetError }) => (
						<ComponentError message={error.message} reset={resetError} />
					)}
				>
					<Suspense fallback={<Loader />}>
						<PaginatedSearchWrapper
							items={data?.Items ?? []}
							itemsPerPage={PAGE_SIZE}
							initialSortOrder="DESC"
							getItemName={(item) => item.Name}
							getItemDate={(item) => item.LastModified}
							isLoading={isPending}
							serverSidePagination={{
								currentPage: page,
								totalPages: Math.ceil((data?.Total ?? 0) / PAGE_SIZE),
								onPageChange: setPage,
								onSearchChange: (value) => {
									setSearch(value);
									setPage(1);
								},
								onSortChange: setSort,
							}}
						>
							{(paginatedItems) =>
								paginatedItems?.map((item, key) => (
									<ActiveProcedureItem item={item} key={key} />
								))
							}
						</PaginatedSearchWrapper>
					</Suspense>
				</Sentry.ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
};
